import React, { useState, useEffect } from 'react';
import { 
  Card, 
  Row, 
  Col, 
  Typography, 
  Select, 
  DatePicker,
  Spin,
  message,
  Empty
} from 'antd';
import {
  LineChart,
  Line,
  AreaChart,
  Area,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';
import { api } from '../utils/api';

const { Title } = Typography;
const { Option } = Select;
const { RangePicker } = DatePicker;

const Analytics = () => {
  const [loading, setLoading] = useState(true);
  const [timeRange, setTimeRange] = useState('7d');
  const [dateRange, setDateRange] = useState(null);
  const [analyticsData, setAnalyticsData] = useState({
    orders: [],
    users: [],
    stocks: []
  });

  useEffect(() => {
    fetchAnalyticsData();
  }, [timeRange, dateRange]);

  const fetchAnalyticsData = async () => {
    setLoading(true);
    try {
      // Fetch real data from existing endpoints
      const [ordersRes, usersRes, stocksRes] = await Promise.all([
        api.getOrders(),
        api.getUsers(),
        api.getStocks()
      ]);

      setAnalyticsData({
        orders: ordersRes.data || [],
        users: usersRes.data || [],
        stocks: stocksRes.data || []
      });
    } catch (error) {
      message.error('Failed to fetch analytics data');
    } finally {
      setLoading(false);
    }
  };

  // Generate real analytics from actual data
  const generateOrderTypeData = () => {
    if (!analyticsData.orders.length) return [];
    
    const orderTypes = analyticsData.orders.reduce((acc, order) => {
      const type = order.orderType || 'market';
      acc[type] = (acc[type] || 0) + 1;
      return acc;
    }, {});

    const colors = { market: '#1890ff', limit: '#52c41a', stop: '#fa8c16' };
    
    return Object.entries(orderTypes).map(([type, count]) => ({
      name: `${type.charAt(0).toUpperCase() + type.slice(1)} Orders`,
      value: count,
      color: colors[type] || '#722ed1'
    }));
  };

  const generateUserGrowthData = () => {
    if (!analyticsData.users.length) return [];
    
    const monthlyUsers = {};
    const now = new Date();
    
    // Initialize last 6 months
    for (let i = 5; i >= 0; i--) {
      const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const monthKey = date.toLocaleDateString('en-US', { month: 'short' });
      monthlyUsers[monthKey] = { newUsers: 0, totalUsers: 0 };
    }
    
    // Count users by registration month
    analyticsData.users.forEach(user => {
      const userDate = new Date(user.createdAt);
      const monthKey = userDate.toLocaleDateString('en-US', { month: 'short' });
      if (monthlyUsers[monthKey]) {
        monthlyUsers[monthKey].newUsers++;
      }
    });
    
    // Calculate cumulative total
    let runningTotal = 0;
    return Object.entries(monthlyUsers).map(([month, data]) => {
      runningTotal += data.newUsers;
      return {
        month,
        newUsers: data.newUsers,
        totalUsers: runningTotal
      };
    });
  };

  const generateTopStocksData = () => {
    if (!analyticsData.orders.length) return [];
    
    const stockTrades = analyticsData.orders.reduce((acc, order) => {
      const symbol = order.symbol;
      if (!acc[symbol]) {
        acc[symbol] = { trades: 0, volume: 0 };
      }
      acc[symbol].trades++;
      acc[symbol].volume += order.quantity || 0;
      return acc;
    }, {});
    
    return Object.entries(stockTrades)
      .map(([symbol, data]) => ({ symbol, ...data }))
      .sort((a, b) => b.trades - a.trades)
      .slice(0, 5);
  };

  const orderTypeData = generateOrderTypeData();
  const userGrowthData = generateUserGrowthData();
  const topStocksData = generateTopStocksData();

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div className="fade-in" style={{ padding: '0 4px' }}>
      {/* Header */}
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'flex-start',
        marginBottom: 32,
        flexWrap: 'wrap',
        gap: 16
      }}>
        <div>
          <Title level={2} style={{ margin: 0, color: '#1e293b', fontSize: '28px', fontWeight: 700 }}>
            Analytics Dashboard
          </Title>
          <p style={{ color: '#64748b', margin: '4px 0 0 0', fontSize: '16px' }}>
            Comprehensive analytics and insights
          </p>
        </div>
        <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
          <Select
            value={timeRange}
            onChange={setTimeRange}
            style={{ width: 140 }}
            size="large"
          >
            <Option value="7d">Last 7 Days</Option>
            <Option value="30d">Last 30 Days</Option>
            <Option value="90d">Last 90 Days</Option>
            <Option value="1y">Last Year</Option>
          </Select>
          <RangePicker
            onChange={setDateRange}
            style={{ width: 280 }}
            size="large"
          />
        </div>
      </div>

      {/* Summary Stats */}
      <Row gutter={[24, 24]} style={{ marginBottom: 32 }}>
        <Col xs={24} sm={12} lg={8}>
          <Card className="slide-up" style={{ 
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            border: 'none',
            color: 'white'
          }}>
            <div style={{ textAlign: 'center' }}>
              <Title level={3} style={{ color: 'white', margin: 0, fontSize: '32px' }}>
                {analyticsData.users.length}
              </Title>
              <p style={{ color: 'rgba(255,255,255,0.8)', margin: '8px 0 0 0', fontSize: '16px' }}>
                Total Users
              </p>
            </div>
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={8}>
          <Card className="slide-up" style={{ 
            background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
            border: 'none',
            color: 'white'
          }}>
            <div style={{ textAlign: 'center' }}>
              <Title level={3} style={{ color: 'white', margin: 0, fontSize: '32px' }}>
                {analyticsData.orders.length}
              </Title>
              <p style={{ color: 'rgba(255,255,255,0.8)', margin: '8px 0 0 0', fontSize: '16px' }}>
                Total Orders
              </p>
            </div>
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={8}>
          <Card className="slide-up" style={{ 
            background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
            border: 'none',
            color: 'white'
          }}>
            <div style={{ textAlign: 'center' }}>
              <Title level={3} style={{ color: 'white', margin: 0, fontSize: '32px' }}>
                {analyticsData.stocks.length}
              </Title>
              <p style={{ color: 'rgba(255,255,255,0.8)', margin: '8px 0 0 0', fontSize: '16px' }}>
                Total Stocks
              </p>
            </div>
          </Card>
        </Col>
      </Row>

      {/* User Growth Chart */}
      <Row gutter={[24, 24]} style={{ marginBottom: 32 }}>
        <Col xs={24} lg={16}>
          <Card 
            title="User Growth"
            className="slide-up"
          >
            {userGrowthData.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={userGrowthData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="month" stroke="#64748b" />
                  <YAxis stroke="#64748b" />
                  <Tooltip 
                    contentStyle={{
                      background: 'white',
                      border: '1px solid #e2e8f0',
                      borderRadius: '8px',
                      boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                    }}
                  />
                  <Legend />
                  <Bar dataKey="newUsers" fill="#3b82f6" name="New Users" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="totalUsers" fill="#10b981" name="Total Users" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <Empty description="No user data available" />
            )}
          </Card>
        </Col>
        <Col xs={24} lg={8}>
          <Card 
            title="Order Types Distribution"
            className="slide-up"
          >
            {orderTypeData.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={orderTypeData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {orderTypeData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip 
                    contentStyle={{
                      background: 'white',
                      border: '1px solid #e2e8f0',
                      borderRadius: '8px',
                      boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                    }}
                  />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <Empty description="No order data available" />
            )}
          </Card>
        </Col>
      </Row>

      {/* Top Stocks */}
      <Row gutter={[24, 24]}>
        <Col span={24}>
          <Card 
            title="Top Traded Stocks"
            className="slide-up"
          >
            {topStocksData.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={topStocksData} layout="horizontal">
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis type="number" stroke="#64748b" />
                  <YAxis dataKey="symbol" type="category" stroke="#64748b" />
                  <Tooltip 
                    contentStyle={{
                      background: 'white',
                      border: '1px solid #e2e8f0',
                      borderRadius: '8px',
                      boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                    }}
                  />
                  <Bar dataKey="trades" fill="#3b82f6" name="Number of Trades" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <Empty description="No trading data available" />
            )}
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default Analytics;